<div class="cipc-verification-report">
  <PageControl
    :title="'CIPC Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      @click="show_export_modal"
    >
      <i class="fas fa-download me-1"></i> Export Data
    </button>

    <!-- Export Modal -->
    <Teleport to="body">
      <div
        class="modal fade"
        :id="'export-modal-' + report_reference"
        tabindex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export CIPC Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-company-info"
                  v-model="export_options.company_information"
                />
                <label class="form-check-label" for="export-company-info">
                  <strong>Company Information</strong>
                  <small class="d-block text-muted"
                    >Company details, registration number, business information,
                    and status</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-director-info"
                  v-model="export_options.director_information"
                />
                <label class="form-check-label" for="export-director-info">
                  <strong>Director Information</strong>
                  <small class="d-block text-muted"
                    >Director details, appointments, and principal
                    information</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-address-info"
                  v-model="export_options.address_information"
                />
                <label class="form-check-label" for="export-address-info">
                  <strong>Address Information</strong>
                  <small class="d-block text-muted"
                    >Physical and postal addresses for the company</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-raw-json"
                  v-model="export_options.raw_json"
                />
                <label class="form-check-label" for="export-raw-json">
                  <strong>Raw JSON Data</strong>
                  <small class="d-block text-muted"
                    >Original unprocessed data from the CIPC verification</small
                  >
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-csv"
                  value="csv"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-csv">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-excel"
                  value="excel"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-excel">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-json"
                  value="json"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-json">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="fas fa-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </PageControl>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-2 header d-flex align-items-center justify-content-between border-bottom mb-2"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h5 fw-bold mb-1">CIPC Verification Report</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm p-2 bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-building me-2"></i>
            CIPC Verification Summary
          </h6>
        </div>
        <div class="card-body">
          <p>
            This report summarizes the CIPC verification for
            <span class="fw-bold text-dark">{{ company_name }}</span>
            (Registration Number:
            <span class="fw-bold text-dark">{{ registration_number }}</span>),
            with
            <span class="fw-bold text-dark">{{ director_count }}</span>
            director(s) (Reference ID:
            <span class="fw-bold text-dark">{{ client_user_reference }}</span>).
          </p>
        </div>
      </div>

      <!-- Report Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-building-user me-2"></i>
                CIPC Verification Report
              </h6>
              <div class="text-muted small">
                Reference: {{ client_user_reference }} | Generated: {{
                formatted_current_date }}
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Company Name:</span>
                    <div class="fw-semibold">{{ company_name }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Registration Number:</span>
                    <div class="fw-semibold">{{ registration_number }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Tax Number:</span>
                    <div class="fw-semibold">{{ tax_number }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern mb-2">Company Status:</span>
                    <div>
                      <span
                        :class="company_status_badge_class"
                        class="status-badge"
                      >
                        <i :class="company_status_icon" class="me-1"></i>
                        {{ commercial_status }}
                      </span>
                    </div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Company Type:</span>
                    <div class="fw-semibold">{{ commercial_type }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Business Age:</span>
                    <div class="fw-semibold">{{ age_of_business }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Business Information Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-briefcase me-2"></i>
                Business Information
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Field</th>
                      <th class="text-xs">Value</th>
                      <th class="text-xs">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="fw-semibold text-xs">Registration Date</td>
                      <td class="text-xs">{{ formatted_registration_date }}</td>
                      <td class="text-xs">
                        Date when company was registered with CIPC
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Business Start Date</td>
                      <td class="text-xs">
                        {{ formatted_business_start_date }}
                      </td>
                      <td class="text-xs">
                        Date when business operations commenced
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Financial Year End</td>
                      <td class="text-xs">{{ financial_year_end }}</td>
                      <td class="text-xs">Month when financial year ends</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">SIC Code</td>
                      <td class="text-xs">{{ sic_code }}</td>
                      <td class="text-xs">
                        Standard Industrial Classification code
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">VAT Number</td>
                      <td class="text-xs">{{ vat_number }}</td>
                      <td class="text-xs">
                        Value Added Tax registration number
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Authorised Shares</td>
                      <td class="text-xs">{{ authorised_shares }}</td>
                      <td class="text-xs">Number of authorised shares</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Issued Shares</td>
                      <td class="text-xs">{{ issued_shares }}</td>
                      <td class="text-xs">Number of shares actually issued</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Director Count</td>
                      <td class="text-xs">{{ director_count }}</td>
                      <td class="text-xs">Total number of directors</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <!-- Directors Information Section - Paginated -->
    <SectionSheet
      v-for="(directorGroup, groupIndex) in paginatedDirectors"
      :key="'directors-' + groupIndex"
    >
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                Directors Information
                <span v-if="paginatedDirectors.length > 1" class="text-muted small">
                  (Page {{ groupIndex + 1 }} of {{ paginatedDirectors.length }})
                </span>
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Full Name</th>
                      <th class="text-xs">ID Number</th>
                      <th class="text-xs">Status</th>
                      <th class="text-xs">Appointment Date</th>
                      <!-- Add other columns as needed -->
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="director in directorGroup" :key="director.IDNo">
                      <td class="fw-semibold text-xs">{{ get_director_full_name(director) }}</td>
                      <td class="text-xs">{{ director.IDNo }}</td>
                      <td class="text-xs">{{ director.DirectorStatusCode }}</td>
                      <td class="text-xs">{{ director.AppointmentDate }}</td>
                      <!-- Add other columns as needed -->
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <!-- Director Contact Information Section - Paginated -->
    <SectionSheet
      v-for="(contactGroup, groupIndex) in paginatedDirectorContacts"
      :key="'contacts-' + groupIndex"
    >
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                Director Contact Information
                <span v-if="paginatedDirectorContacts.length > 1" class="text-muted small">
                  (Page {{ groupIndex + 1 }} of {{ paginatedDirectorContacts.length }})
                </span>
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Full Name</th>
                      <th class="text-xs">Cellular Number</th>
                      <th class="text-xs">Email Address</th>
                      <th class="text-xs">Home Telephone</th>
                      <th class="text-xs">Work Telephone</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="director in contactGroup" :key="director.IDNo + '_contact'">
                      <td class="fw-semibold text-xs">{{ get_director_full_name(director) }}</td>
                      <td class="text-xs">{{ get_director_contact_info(director).cellular }}</td>
                      <td class="text-xs">{{ get_director_contact_info(director).email }}</td>
                      <td class="text-xs">{{ get_director_contact_info(director).home_phone }}</td>
                      <td class="text-xs">{{ get_director_contact_info(director).work_phone }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Address Information Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-map-marker-alt me-2"></i>
                Address Information
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Address Type</th>
                      <th class="text-xs">Address</th>
                      <th class="text-xs">Postal Code</th>
                      <th class="text-xs">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="address in address_information"
                      :key="address.AddressType"
                    >
                      <td class="fw-semibold text-xs">
                        {{ address.AddressType }}
                      </td>
                      <td class="text-xs">{{ format_address(address) }}</td>
                      <td class="text-xs">{{ address.PostalCode || 'N/A' }}</td>
                      <td class="text-xs">
                        {{ formatDate(address.LastUpdatedDate) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <div class="card shadow-sm">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Disclaimer
          </h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-info-circle me-2"></i>General Information
            </h6>
            <p class="text-xs mb-4">
              This CIPC Verification Report is prepared solely for the
              benefit of the intended recipient and is based on data obtained
              from the Companies and Intellectual Property Commission (CIPC).
              While
              <strong> THE GOOD PEOPLE DATA COMPANY (PTY) LTD ('TGPDC')</strong>
              employs industry-standard practices to collect and process
              information, TGPDC does not guarantee, and expressly disclaims,
              the accuracy, completeness or timeliness of any data contained
              herein. The Report reflects the status of each record at the
              moment of generation and may not reflect subsequent changes.
            </p>
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock me-2"></i>Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All company and personal information is processed in accordance
              with applicable data protection laws including POPIA (Protection
              of Personal Information Act). This report should only be shared
              with authorized personnel who require access to this information
              for legitimate business purposes.
            </p>
            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>